import React from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { EGG_IDS } from '../lib/eggs';

// "How to Navigate": every control in the gallery, for phones/tablets or for computers.
const secretsFound = () => {
  try { return JSON.parse(localStorage.getItem('gallery-secrets') || '[]').length; } catch (e) { return 0; }
};

const Row = ({ k, children }) => (
  <div className="flex items-start gap-3">
    <span className="font-semibold text-amber-700 min-w-[8.5rem] shrink-0">{k}</span>
    <span>{children}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-lg font-semibold text-amber-800 mb-3">{title}</h3>
    <div className="space-y-2.5 text-gray-700 text-sm">{children}</div>
  </div>
);

const Key = ({ children }) => (
  <kbd className="inline-block min-w-[1.6rem] text-center px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 font-mono text-xs">{children}</kbd>
);

// The animated line that invites visitors to hunt for the secrets.
export const SecretsTeaser = ({ found = 0, total = EGG_IDS.length }) => (
  <div className="secrets-teaser rounded-lg px-4 py-3 flex items-center gap-3">
    <span className="egg-wobble text-2xl" aria-hidden="true">🥚</span>
    <div className="flex-1">
      <div className="egg-shimmer font-semibold leading-snug">
        This gallery hides {total} secrets. Can you find them all?
      </div>
      <div className="text-xs text-amber-900/80 mt-0.5">
        Try clicking things, staying still, typing words, visiting at night… and come back on festival days.
        {found > 0 && <> You've found <strong>{found} of {total}</strong>.</>}
      </div>
    </div>
  </div>
);

const HelpModal = ({ isOpen, onClose, isMobile }) => {
  if (!isOpen) return null;
  const found = secretsFound();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl border border-gray-200 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="How to Navigate"
      >
        {/* Header with Close Button */}
        <div className="sticky top-0 z-10 bg-white/95 border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">How to Navigate</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-600 hover:bg-gray-100" aria-label="Close">
            <X size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <SecretsTeaser found={found} />

          {isMobile ? (
            <Section title="📱 Moving around">
              <Row k="Walk stick (bottom left)">Push it to walk in any direction. The further you push, the faster you go. You always stay at eye level.</Row>
              <Row k="Look stick (bottom right)">Look around, up at the glass roof or down at the floor. Dragging with one finger works too.</Row>
              <Row k="Tap the floor">Walk to that spot.</Row>
              <Row k="Tap a window">Walk up to it and look out at the meadow, the animals and the weather.</Row>
              <Row k="Tap the glass door">Walk into the Grand Salon, the second room (or use 🚪). Tap it again to come back.</Row>
            </Section>
          ) : (
            <Section title="🖥️ Moving around">
              <Row k={<><Key>W</Key> <Key>A</Key> <Key>S</Key> <Key>D</Key> / arrows</>}>Walk forward, left, back and right. You always stay at eye level.</Row>
              <Row k="Click + drag">Look around, up at the glass roof or down at the floor.</Row>
              <Row k="Mouse wheel">Walk forward or back.</Row>
              <Row k="Click the floor">Walk to that spot.</Row>
              <Row k="Click a window">Walk up to it and look out at the meadow, the animals and the weather.</Row>
              <Row k="Click the glass door">Walk into the Grand Salon, the second room (or use 🚪). Click it again to come back.</Row>
              <Row k="On-screen pad">The arrows at the bottom left walk too.</Row>
            </Section>
          )}

          <Section title="🖼️ Paintings">
            <Row k={isMobile ? 'Tap a painting' : 'Click a painting'}>Walk up to it and see its title.</Row>
            <Row k={isMobile ? 'Double-tap' : 'Double-click'}>Open the full-screen viewer to zoom into the details.</Row>
            <Row k="Bottom card">← → for the previous / next painting, ♥ to like it.</Row>
            <Row k="ⓘ (top right)">Details: the story, the process video, <em>Inquire / Buy a print</em> and sharing.</Row>
            <Row k="☰ (top right)">The whole collection at a glance.</Row>
          </Section>

          <Section title="🎛️ Buttons">
            <div className="text-xs uppercase tracking-wide text-gray-500">Top left</div>
            <Row k="Profile picture">The artist's page.</Row>
            <Row k="❤️">Support the artist.</Row>
            <Row k="?">This guide.</Row>
            <Row k="📖">The guestbook: read and leave messages.</Row>
            <div className="text-xs uppercase tracking-wide text-gray-500 pt-1">Top right</div>
            <Row k="👣 Tour">A guided walk from painting to painting, with music.</Row>
            <Row k="🚪 Door">Go to the Grand Salon and back.</Row>
            <Row k="🎬 Theatre">Sit down and watch the videos of how the paintings were made.</Row>
            <Row k="🌅 Time of day">Morning, afternoon, evening or night. The gallery starts at your own time of day.</Row>
            <Row k="☀️ Weather">Clear, cloudy, windy, rain, storm, snow or fog, or your live local weather (it starts with it). Weather sounds can be switched off here.</Row>
            {!isMobile && <Row k="⤢ Fullscreen">Fill the whole screen.</Row>}
            <Row k="▶ Music">Play or pause Vivaldi's <em>Spring</em>{isMobile ? '.' : '; hover for the volume.'}</Row>
          </Section>

          {!isMobile && (
            <Section title="⌨️ Keyboard shortcuts">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Key>T</Key> Guided tour on / off</div>
                <div><Key>N</Key> Next time of day</div>
                <div><Key>L</Key> List of paintings</div>
                <div><Key>I</Key> Painting details</div>
                <div><Key>F</Key> Fullscreen</div>
                <div><Key>H</Key> This guide</div>
                <div><Key>Esc</Key> Close this guide, stop the tour, leave fullscreen or the theatre</div>
                <div><Key>←</Key> <Key>→</Key> Previous / next video in the theatre</div>
              </div>
            </Section>
          )}

          {/* Tips */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold text-amber-900 mb-2">💡 Tips</h4>
            <ul className="text-sm text-amber-900 space-y-1">
              <li>• Watch the meadow through the windows: deer, foxes and birds change with the weather and the time of day.</li>
              <li>• Share a painting's link and it opens right in front of that painting.</li>
              <li>• On festival days the gallery is decorated. Look out for lanterns, lamps and fireworks.</li>
              <li>• Sign the guestbook before you leave.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
